import {
  useCallback,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { UploadCloud } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface DropzoneProps {
  accept?: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
  title?: string;
  description?: string;
  icon?: ReactNode;
}

export function Dropzone({
  accept,
  multiple,
  onFiles,
  disabled,
  className,
  title = 'Перетащите файл сюда',
  description = 'или нажмите, чтобы выбрать',
  icon,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    (list: FileList | null) => {
      if (!list || list.length === 0) return;
      const arr = Array.from(list);
      onFiles(arr);
    },
    [onFiles],
  );

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    setDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'group relative flex cursor-pointer select-none flex-col items-center justify-center gap-3 overflow-hidden rounded-[20px] border-2 border-dashed px-6 py-10 text-center',
        'transition-all duration-300 ease-[var(--ease-out-soft)]',
        dragging
          ? 'border-brand-primary bg-bg-soft scale-[1.01]'
          : 'border-stroke bg-bg-soft/50 hover:border-brand-primary/60 hover:bg-bg-soft',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      {/* Subtle radial highlight that follows the dropzone state */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500',
          dragging && 'opacity-100',
        )}
        style={{
          background:
            'radial-gradient(circle at center, rgba(255, 174, 66, 0.20), transparent 60%)',
        }}
      />

      <div
        className={cn(
          'relative flex h-14 w-14 items-center justify-center rounded-full bg-brand-gradient text-white shadow-soft',
          'transition-transform duration-300 ease-[var(--ease-spring)]',
          dragging ? 'scale-110' : 'group-hover:scale-105',
        )}
      >
        {/* Glow behind the icon when dragging */}
        {dragging && (
          <span
            className="absolute inset-0 rounded-full bg-brand-gradient opacity-60 blur-md animate-pulse-soft"
            aria-hidden
          />
        )}
        <span className="relative">{icon ?? <UploadCloud size={26} />}</span>
      </div>
      <div className="relative">
        <p className="text-base font-semibold tracking-tight text-text">
          {dragging ? 'Отпустите файл — добавим' : title}
        </p>
        <p className="mt-0.5 text-sm text-text-muted">{description}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
